'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../../app-ui-visual'
import { client, MONTH_PLANNING, teamMember, type MonthState } from '../../demo-data'
import { PhoneFrame } from '../../frames'
import { useVisualCopy, type VisualCopy } from '../../visual-copy'
import styles from '../mobile-visual.module.css'
import { useScenario } from '../use-scenario'
import {
  effectiveBeat,
  Field,
  Icon,
  NotificationBanner,
  StatusBar,
  StatusStepRow,
  statusSteps,
  TabBar,
  within,
  type Cell,
  type RowSpec,
  type Tone,
} from './chrome'

/** The three checklist lines the detail pane shows, from the shared bookkeeping checklist. */
const tasks = (t: VisualCopy) => t.content.bookkeepingChecklist.slice(0, 3)

const TONE_STYLE: Record<Tone, { background: string; color: string; border: string }> = {
  done: { background: 'var(--mv-done-bg)', color: 'var(--mv-done-fg)', border: '1px solid transparent' },
  busy: { background: 'var(--mv-busy-bg)', color: 'var(--mv-busy-fg)', border: '1px solid transparent' },
  recv: { background: 'var(--mv-recv-bg)', color: 'var(--mv-recv-fg)', border: '1px solid transparent' },
  open: { background: 'transparent', color: 'var(--mv-text2)', border: '1px dashed var(--mv-dash)' },
  none: { background: 'transparent', color: 'var(--mv-text2)', border: '1px solid transparent' },
}

/** Phone-table tone per monthly state — the same states the desktop month boards render. */
const MONTH_TONE: Record<MonthState, Tone> = {
  klaar: 'done',
  loopt: 'busy',
  ontvangen: 'recv',
  gepland: 'open',
  leeg: 'none',
}

// The phone's "maandplanning" table IS the canonical month planning — same clients, same order,
// same states as the desktop month boards, just two columns wide. Only row 0's first cell changes
// through the scenario — see `CHANGE_ROW`/`CHANGE_WINDOW`.
const ROWS: readonly RowSpec[] = MONTH_PLANNING.map((row) => {
  const { name, form } = client(row.nr)
  const cell = (index: number): Cell => ({
    label: row.cells[index] === 'leeg' ? '—' : (row.assignees[index] ?? '—'),
    tone: MONTH_TONE[row.cells[index]],
  })
  return { name, nr: row.nr, tag: form, c1: cell(0), c2: cell(1) }
})

// The row the scenario taps into: the client whose detail screen the phone then opens. Derived
// from the data so re-ordering the month planning cannot desynchronise the two.
const DETAIL_CLIENT = client('10010')
const CHANGE_ROW = MONTH_PLANNING.findIndex((row) => row.nr === DETAIL_CLIENT.nr)
/** Who owns that client's bookkeeping — the same colleague the desktop month board shows. */
const DETAIL_OWNER = teamMember(MONTH_PLANNING[CHANGE_ROW].assignees[0] ?? 'HM')
const HIGHLIGHT_ROW = CHANGE_ROW

// The audit trail of one client's month — who signed each step off and when, straight from the
// shared workflow.

const TOTAL_BEATS = 13

const DETAIL_WINDOW: readonly [number, number] = [3, 11]
// Runs to the end of the loop, past the detail pane's close at 11: the round-trip payoff is the
// returned *list* showing the flipped cell and updated counters during beats 11–12. Ending both
// windows on the same beat reverted the change in the exact frame the list faded back in, so the
// visitor never saw it land.
const CHANGE_WINDOW: readonly [number, number] = [6, 13]
const NOTIFICATION_WINDOW: readonly [number, number] = [7, 10]
const SELECT_BEAT = 2

function SegmentBar({ busyWidth }: { busyWidth: number }) {
  return (
    <div className="flex h-[6px] overflow-hidden rounded-full" style={{ background: 'var(--mv-seg-grey)' }}>
      <span style={{ width: '8%', background: 'var(--mv-seg-grey)' }} />
      <span style={{ width: '9%', background: 'var(--mv-seg-recv)' }} />
      <span className={styles.width} style={{ width: `${busyWidth}%`, background: 'var(--mv-seg-busy)' }} />
      <span style={{ width: '6%', background: 'var(--mv-seg-sig)' }} />
      <span className="flex-1" style={{ background: 'var(--mv-seg-done)' }} />
    </div>
  )
}

const legend = (t: VisualCopy) =>
  [
    { label: t.workflow['ontvangen'], color: 'var(--mv-seg-recv)' },
    { label: t.workflow['uitvoering'], color: 'var(--mv-seg-busy)' },
    { label: t.workflow['afgerond'], color: 'var(--mv-seg-done)' },
  ] as const

function SummaryCard({ busyWidth }: { busyWidth: number }) {
  const { t } = useVisualCopy()
  return (
    <div
      className="flex flex-none flex-col gap-[7px] rounded-[12px] px-[11px] py-[9px]"
      style={{ background: 'var(--mv-card)' }}
    >
      <div className="flex items-start justify-around">
        <div className="flex flex-col items-center">
          <b className="text-[15px] font-extrabold tabular-nums">533/793</b>
          <span className="text-[7.5px]" style={{ color: 'var(--mv-text2)' }}>
            {t.word.tasks}
          </span>
        </div>
        <div className="flex flex-col items-center">
          <b className="text-[15px] font-extrabold tabular-nums" style={{ color: 'var(--mv-accent)' }}>
            67%
          </b>
          <span className="text-[7.5px]" style={{ color: 'var(--mv-text2)' }}>
            {t.word.complete}
          </span>
        </div>
        <div className="flex flex-col items-center">
          <b className="text-[15px] font-extrabold tabular-nums">58</b>
          <span className="text-[7.5px]" style={{ color: 'var(--mv-text2)' }}>
            {t.screen.mobile.ownTasks}
          </span>
        </div>
      </div>
      <SegmentBar busyWidth={busyWidth} />
      <div className="flex items-center gap-2.5 text-[7.5px]" style={{ color: 'var(--mv-text2)' }}>
        {legend(t).map((item) => (
          <span key={item.label} className="inline-flex items-center gap-1">
            <span className="size-[6px] rounded-full" style={{ background: item.color }} />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  )
}

function Pill({ label, active }: { label: string; active?: boolean }) {
  return (
    <span
      className="flex-1 rounded-full py-[5px] text-center text-[9px] font-bold"
      style={{
        color: active ? 'var(--mv-accent)' : 'var(--mv-text2)',
        background: active ? 'var(--mv-accent-soft)' : 'var(--mv-card)',
        fontWeight: active ? 700 : 600,
      }}
    >
      {label}
    </span>
  )
}

function MiniTrack({ label, pct }: { label: string; pct: number }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-[7px] font-bold" style={{ color: 'var(--mv-text2)' }}>
        {label}
      </span>
      <span className="h-[2.5px] w-[26px] overflow-hidden rounded-full" style={{ background: 'var(--mv-seg-grey)' }}>
        <span className="block h-full" style={{ width: `${pct}%`, background: 'var(--mv-accent)' }} />
      </span>
    </div>
  )
}

function ClientRow({ row, index, beat }: { row: RowSpec; index: number; beat: number }) {
  const c1Tone = index === CHANGE_ROW && within(beat, CHANGE_WINDOW) ? 'done' : row.c1.tone
  const highlighted = index === HIGHLIGHT_ROW && beat === SELECT_BEAT
  const c1Style = TONE_STYLE[c1Tone]
  const c2Style = TONE_STYLE[row.c2.tone]

  return (
    <div
      className={clsx('grid grid-cols-[1.5fr_1fr_1fr] items-center gap-1.5 rounded-[9px] px-1.5 py-1', styles.tone)}
      style={{ background: highlighted ? 'var(--mv-accent-soft)' : 'transparent' }}
    >
      <div className="flex min-w-0 flex-col gap-px">
        <span className="truncate text-[10.5px] font-semibold" style={{ color: 'var(--mv-text)' }}>
          {row.name}
        </span>
        <span className="flex items-center gap-1">
          <span className="text-[7.5px] tabular-nums" style={{ color: 'var(--mv-text2)' }}>
            #{row.nr}
          </span>
          <span
            className="rounded-[4px] px-1 py-px text-[7.5px] font-bold tracking-[0.3px]"
            style={{ color: 'var(--mv-accent)', background: 'var(--mv-accent-soft)' }}
          >
            {row.tag}
          </span>
        </span>
      </div>
      <span
        className={clsx(
          'flex h-[26px] w-[34px] items-center justify-center justify-self-center rounded-[7px] text-[9px] font-bold',
          styles.tone,
        )}
        style={{ background: c1Style.background, color: c1Style.color, border: c1Style.border }}
      >
        {c1Tone === 'none' ? '–' : row.c1.label}
      </span>
      <span
        className="flex h-[26px] w-[34px] items-center justify-center justify-self-center rounded-[7px] text-[9px] font-bold"
        style={{ background: c2Style.background, color: c2Style.color, border: c2Style.border }}
      >
        {row.c2.tone === 'none' ? '–' : row.c2.label}
      </span>
    </div>
  )
}

function ListPane({
  detailOpen,
  busyWidth,
  openCount,
  doneCount,
  beat,
}: {
  detailOpen: boolean
  busyWidth: number
  openCount: number
  doneCount: number
  beat: number
}) {
  const { t } = useVisualCopy()
  // The third pill drops both filters, so it counts what the other two split between them.
  const totalActivities = openCount + doneCount
  return (
    <div className={clsx('flex h-full flex-col gap-1.5', styles.fade)} style={{ opacity: detailOpen ? 0 : 1 }}>
      <SummaryCard busyWidth={busyWidth} />
      <div className="flex flex-none gap-1">
        <Pill label={`${t.word.open} ${openCount}`} active />
        <Pill label={`${t.workflow.afgerond} ${doneCount}`} />
        <Pill label={t.screen.mobile.allCount(totalActivities)} />
      </div>
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[11px] px-2.5"
        style={{ background: 'var(--mv-card)' }}
      >
        <div
          className="grid grid-cols-[1.5fr_1fr_1fr] items-center gap-1.5 border-b py-1.5"
          style={{ borderColor: 'var(--mv-hairline)' }}
        >
          <span className="text-[7.5px] font-bold tracking-[0.3px]" style={{ color: 'var(--mv-text2)' }}>
            {t.word.client.toUpperCase()}
          </span>
          <MiniTrack label={t.screen.mobile.bookkeepingShort} pct={93} />
          <MiniTrack label={t.screen.mobile.vatShort} pct={95} />
        </div>
        <div className="flex flex-1 flex-col justify-evenly">
          {ROWS.map((row, index) => (
            <ClientRow key={row.nr} row={row} index={index} beat={beat} />
          ))}
        </div>
      </div>
    </div>
  )
}

function DetailPane({
  detailOpen,
  stap,
  activeStatusIndex,
  statusDone,
}: {
  detailOpen: boolean
  stap: number
  activeStatusIndex: number
  statusDone: boolean
}) {
  const { t, fmt } = useVisualCopy()
  return (
    <div
      className={clsx(
        'absolute inset-0 flex flex-col gap-2.5 overflow-hidden rounded-[14px] px-2 py-0.5',
        styles.slideX,
      )}
      style={{
        background: 'var(--mv-scr)',
        opacity: detailOpen ? 1 : 0,
        transform: detailOpen ? 'translateX(0)' : 'translateX(18px)',
        pointerEvents: 'none',
      }}
    >
      <div
        className="flex flex-col gap-[9px] rounded-[12px] px-[11px] py-[10px]"
        style={{ background: 'var(--mv-card)' }}
      >
        <div className="flex items-center gap-[9px]">
          <span
            className="flex size-[30px] shrink-0 items-center justify-center rounded-[9px]"
            style={{ background: 'var(--mv-icon-bg)', color: 'var(--mv-text2)' }}
          >
            <Icon size={15} strokeWidth={1.6}>
              <rect x="3.5" y="7.5" width="17" height="12" rx="2.5" />
              <path d="M8.5 7.5V6a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1.5M3.5 12.5h17" />
            </Icon>
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-[11px] font-bold">
              {DETAIL_CLIENT.name}{' '}
              <span
                className="rounded-[5px] px-[5px] py-[1.5px] text-[7.5px] font-bold"
                style={{ color: 'var(--mv-busy-fg)', background: 'var(--mv-busy-bg)' }}
              >
                {DETAIL_CLIENT.form}
              </span>
            </span>
            <span className="text-[8px] tabular-nums" style={{ color: 'var(--mv-text2)' }}>
              #{DETAIL_CLIENT.nr}
            </span>
          </div>
          <span
            className="flex size-6 shrink-0 items-center justify-center rounded-full text-[8px] font-bold"
            style={{ background: 'var(--mv-busy-bg)', color: 'var(--mv-busy-fg)' }}
          >
            {DETAIL_OWNER.initials}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-x-2.5 gap-y-2">
          <Field label={t.screen.mobile.period} value={t.period.monthLower} />
          <Field label={t.word.owner.toUpperCase()} value={DETAIL_OWNER.name} />
          <Field label={t.screen.mobile.timeSpent} value={`1 ${t.word.hoursUnit}`} />
          <Field label={t.screen.mobile.amount} value={fmt.euro(85, true)} />
        </div>
      </div>

      <div className="flex items-center justify-between px-0.5">
        <span className="text-[8px] font-bold tracking-[0.5px]" style={{ color: 'var(--mv-text2)' }}>
          STATUS
        </span>
        <span className="text-[8px] font-semibold" style={{ color: 'var(--mv-text2)' }}>
          {t.screen.mobile.step(stap, 4)}
        </span>
      </div>
      <div className="flex flex-col rounded-[12px] px-3" style={{ background: 'var(--mv-card)' }}>
        {statusSteps(t, fmt).map((step, index) => (
          <StatusStepRow
            key={step.label}
            step={step}
            index={index}
            activeIndex={activeStatusIndex}
            done={index === 3 && statusDone}
            isLast={index === statusSteps(t, fmt).length - 1}
          />
        ))}
      </div>

      <div className="flex items-center justify-between px-0.5">
        <span className="text-[8px] font-bold tracking-[0.5px]" style={{ color: 'var(--mv-text2)' }}>
          {t.screen.mobile.activities}
        </span>
        <span className="text-[8px] font-bold" style={{ color: 'var(--mv-accent)' }}>
          {tasks(t).length} / {tasks(t).length} {t.word.complete}
        </span>
      </div>
      <div className="flex flex-col rounded-[12px] px-3" style={{ background: 'var(--mv-card)' }}>
        {tasks(t).map((label, index) => (
          <div
            key={label}
            className="flex items-center gap-2.5 py-2"
            style={{ borderBottom: index === tasks(t).length - 1 ? 'none' : '1px solid var(--mv-hairline)' }}
          >
            <span
              className="flex size-[18px] shrink-0 items-center justify-center rounded-[6px] text-white"
              style={{ background: 'var(--mv-done-fg)' }}
            >
              <Icon size={10} strokeWidth={3}>
                <path d="M5 12l5 5 9-10" />
              </Icon>
            </span>
            <span
              className="min-w-0 flex-1 truncate text-[10.5px] font-medium line-through"
              style={{ color: 'var(--mv-text2)' }}
            >
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function PlanningPhone({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(TOTAL_BEATS, { intervalMs: 950 })
  const effBeat = effectiveBeat(beat)

  const detailOpen = within(effBeat, DETAIL_WINDOW)
  const changed = within(effBeat, CHANGE_WINDOW)
  const notify = within(effBeat, NOTIFICATION_WINDOW)
  const activeStatusIndex = changed ? 3 : 1
  const openCount = changed ? 543 : 544
  const doneCount = changed ? 250 : 249
  const busyWidth = changed ? 20 : 22
  const stap = changed ? 4 : 2

  return (
    <AppUiVisual label={t.screen.mobile.captionPlanning} className={className}>
      <PhoneFrame screenClassName={clsx(styles.root, styles.phoneScreen)}>
        <div
          ref={ref}
          className={clsx(styles.root, 'relative flex h-full flex-col')}
          style={{ background: 'var(--mv-scr)', color: 'var(--mv-text)' }}
        >
          <StatusBar detailOpen={detailOpen} title={detailOpen ? t.word.activity : t.screen.monthPlanning.title} />
          <NotificationBanner visible={notify} clientName={DETAIL_CLIENT.name} />

          <div className="relative min-h-0 flex-1 px-2 pt-0.5 pb-1.5">
            <ListPane
              detailOpen={detailOpen}
              busyWidth={busyWidth}
              openCount={openCount}
              doneCount={doneCount}
              beat={effBeat}
            />
            <DetailPane
              detailOpen={detailOpen}
              stap={stap}
              activeStatusIndex={activeStatusIndex}
              statusDone={changed}
            />
          </div>

          <TabBar activeIndex={0} />
        </div>
      </PhoneFrame>
    </AppUiVisual>
  )
}

// ---------------------------------------------------------------------------------------------
// Uren variant (Anim Mobiel soort="uren") — week list → registration form → back. Rest step 8 =
// the filled registration form.
// ---------------------------------------------------------------------------------------------

// The week strip above the form, derived from the shared phone data: weekday letter, day number,
// and what was booked that day — nothing on the weekend, a full day on every working day.
